import { PHASE_PRODUCTION_BUILD } from "next/constants";

// Endpoints that return the same payload for every page of the build. They are
// pinned in the cache and never evicted, because every page needs them.
const SHARED_ENDPOINT_PATTERNS = [
  "/menus/v1/menus/",
  "/aunds/theme-options",
  "/getFrontpage",
  "/aunds/template-options",
  "/aunds/template-css",
  "/aunds/template-routing",
  "/insta/",
  "/wp/v2/categories",
  "/wp/v2/press_category",
  "/options/options",
];

type CachedResponse = {
  status: number;
  headers: [string, string][];
  body: ArrayBuffer;
};

// Every GET is cached during the build, not only the shared endpoints: the same
// post lists and slug lookups are requested over and over while a worker walks
// through its pages, and WP content is treated as immutable for the duration of
// one build anyway (ISR picks up later changes). Non-shared entries are evicted
// FIFO once the cache exceeds the byte budget, so long builds can't grow
// unbounded.
const MAX_CACHE_BYTES =
  Math.max(0, Number(process.env.WP_BUILD_CACHE_MAX_MB) || 256) * 1024 * 1024;

const buildCache = new Map<string, CachedResponse>();
const pinnedKeys = new Set<string>();
const inFlight = new Map<string, Promise<CachedResponse>>();
let cachedBytes = 0;

const isBuildTime = () => process.env.NEXT_PHASE === PHASE_PRODUCTION_BUILD;

const cacheKey = (input: RequestInfo | URL, init?: RequestInit) =>
  `${String(input)}|${init?.method ?? "GET"}`;

const isSharedEndpoint = (input: RequestInfo | URL) => {
  const url = String(input);
  return SHARED_ENDPOINT_PATTERNS.some((pattern) => url.includes(pattern));
};

const evictUntilUnderBudget = () => {
  if (cachedBytes <= MAX_CACHE_BYTES) return;
  for (const [key, entry] of buildCache) {
    if (cachedBytes <= MAX_CACHE_BYTES) break;
    if (pinnedKeys.has(key)) continue;
    buildCache.delete(key);
    cachedBytes -= entry.body.byteLength;
  }
};

const storeInCache = (key: string, entry: CachedResponse, pinned: boolean) => {
  buildCache.set(key, entry);
  cachedBytes += entry.body.byteLength;
  if (pinned) pinnedKeys.add(key);
  evictUntilUnderBudget();
};

const toResponse = (entry: CachedResponse) =>
  new Response(entry.body, { status: entry.status, headers: entry.headers });

// Caps how many WP requests a single static-generation worker process fires
// at once. Each page's getStaticProps fans out ~15-20 parallel requests, and
// Next spawns several worker processes, so without this gate the backend
// sees (workers × requests-per-page) simultaneous requests and falls over.
// Only applied at build time — runtime/ISR traffic is left ungated.
const WP_FETCH_CONCURRENCY = Math.max(
  1,
  Number(process.env.WP_FETCH_CONCURRENCY) || 6,
);

let activeFetches = 0;
const fetchQueue: Array<() => void> = [];

const acquireFetchSlot = (): Promise<void> => {
  if (activeFetches < WP_FETCH_CONCURRENCY) {
    activeFetches++;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    fetchQueue.push(() => {
      activeFetches++;
      resolve();
    });
  });
};

const releaseFetchSlot = () => {
  activeFetches--;
  fetchQueue.shift()?.();
};

export const fetchFromVolatileServer = async (
  input: RequestInfo | URL,
  init?: RequestInit,
  retries: number = 3,
): ReturnType<typeof fetch> => {
  const doFetch = async (remainingRetries: number): ReturnType<typeof fetch> =>
    fetch(input, init).then(undefined, (error) => {
      // eslint-disable-next-line no-console
      console.log(
        "fetchFromVolatileServer",
        remainingRetries,
        error?.code,
        input,
      );
      if (remainingRetries > 0)
        return new Promise<Response>((res, rej) =>
          setTimeout(() => doFetch(remainingRetries - 1).then(res, rej), 1000),
        );
      throw error;
    });

  const method = init?.method ?? "GET";

  if (!isBuildTime() || method !== "GET") {
    return doFetch(retries);
  }

  const key = cacheKey(input, init);

  const cached = buildCache.get(key);
  if (cached) return toResponse(cached);

  // Identical URLs requested concurrently — the same slug resolved by several
  // code paths, or one endpoint needed by several modules of a page — share a
  // single request instead of racing each other.
  const pending = inFlight.get(key);
  if (pending) return toResponse(await pending);

  const request = (async (): Promise<CachedResponse> => {
    await acquireFetchSlot();
    try {
      const res = await doFetch(retries);
      const entry: CachedResponse = {
        status: res.status,
        headers: [...res.headers.entries()],
        body: await res.arrayBuffer(),
      };
      // 404s are cached too: a slug that does not exist in WP will not appear
      // mid-build, and those lookups are among the slowest responses. Other
      // error statuses (5xx, 429) are transient and must stay retryable.
      if (res.ok || res.status === 404)
        storeInCache(key, entry, isSharedEndpoint(input));
      return entry;
    } finally {
      releaseFetchSlot();
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, request);

  return toResponse(await request);
};
